import { Key } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Plus, Trash2, Document } from 'blaze-ui/icons/20'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { Heart, Video, Email } from 'blaze-ui/icons/24'
import { WordPressBrand, MailchimpBrand, GoHighLevelBrand, WixBrand, ShopifyBrand } from 'blaze-ui/icons/35'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { ProviderIcon } from 'screens/Calendar/WeekViewV2/CalendarEventCard/Icon'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { TACTIC_TO_PROVIDER } from 'screens/blaze/Home/VirtualMarketerHome/Brain/PlatformSection/Types'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { BrandPlanTactic } from 'types'
import { TopicConfiguratorProps, TopicRow, PostType } from './Types'
import { getDefaultTacticsForPostType } from './utils'
import { Image } from '../components/Image'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { useCreateMediaFile } from 'hooks/queries/mediaFiles/useCreateMediaFile'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import {
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { ThemeLoadingRow } from 'screens/ContentPlan/CreateCampaignModal/ThemeLoadingRow'
import Styles from './TopicConfigurator.module.scss'

const POST_TYPE_OPTIONS: {
  value: PostType
  label: string
  icon: React.FC<{ size?: number; color?: string }>
  color: string
}[] = [
  { value: 'social_post', label: 'Social Post', icon: Heart, color: '#FD4242' },
  { value: 'video', label: 'Video', icon: Video, color: '#6A00FF' },
  { value: 'blog_post', label: 'Blog Post', icon: Document, color: 'rgba(32,161,79,1)' },
  { value: 'email', label: 'Email', icon: Email, color: 'rgba(255,174,0,1)' },
]

const SOCIAL_TACTICS = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
]
const VIDEO_TACTICS = [BrandPlanTactic.TikTok, BrandPlanTactic.YouTube, BrandPlanTactic.InstagramReel]

export const TOPIC_LOADING_PHASES = [
  'Reviewing campaign...',
  'Analyzing topics...',
  'Proposing alternatives...',
  'Weighing options...',
]

const SocialChannelDropdown = ({
  tactics,
  scheduleTactics,
  onToggle,
}: {
  tactics: BrandPlanTactic[]
  scheduleTactics: BrandPlanTactic[]
  onToggle: (tactic: BrandPlanTactic) => void
}) => {
  const activeTactics = tactics.filter((t) => SOCIAL_TACTICS.includes(t))
  const displayTactics =
    activeTactics.length > 0 ? activeTactics : scheduleTactics.filter((t) => SOCIAL_TACTICS.includes(t))

  return (
    <MenuTrigger placement="bottom-end">
      <Button variant="secondary" withChevron="down" className={Styles.channelsButton}>
        {displayTactics.map((tactic) => {
          const mapping = TACTIC_TO_PROVIDER.find((t) => t.tactic === tactic)
          if (!mapping) return null
          return (
            <ProviderIcon
              key={tactic}
              provider={mapping.provider}
              size={18}
              isHovered={false}
              zIndex={0}
              borderColor="rgba(247, 247, 247, 1)"
              marginLeft={0}
            />
          )
        })}
      </Button>
      <Menu
        aria-label="Social platforms"
        onAction={(key: Key) => onToggle(key as BrandPlanTactic)}
        showCheckmark
        selectionMode="multiple"
        selectedKeys={new Set(activeTactics)}
        disabledKeys={activeTactics.length === 1 ? [activeTactics[0]] : undefined}
      >
        <Item key="instagram" textValue="Instagram" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.Instagram} size={18} isHovered={false} marginLeft={0} />
          Instagram
        </Item>
        <Item key="facebook" textValue="Facebook" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.Facebook} size={18} marginLeft={0} />
          Facebook
        </Item>
        <Item key="linkedin" textValue="LinkedIn" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.LinkedIn} size={18} marginLeft={0} />
          LinkedIn
        </Item>
        <Item key="twitter" textValue="X" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.X} size={18} marginLeft={0} />X
        </Item>
      </Menu>
    </MenuTrigger>
  )
}

const VideoChannelDropdown = ({
  tactics,
  scheduleTactics,
  onToggle,
}: {
  tactics: BrandPlanTactic[]
  scheduleTactics: BrandPlanTactic[]
  onToggle: (tactic: BrandPlanTactic) => void
}) => {
  const activeTactics = tactics.filter((t) => VIDEO_TACTICS.includes(t))
  const displayTactics =
    activeTactics.length > 0 ? activeTactics : scheduleTactics.filter((t) => VIDEO_TACTICS.includes(t))

  return (
    <MenuTrigger placement="bottom-end">
      <Button variant="secondary" withChevron="down" className={Styles.channelsButton}>
        {displayTactics.map((tactic) => {
          const mapping = TACTIC_TO_PROVIDER.find((t) => t.tactic === tactic)
          if (!mapping) return null
          return (
            <ProviderIcon
              key={tactic}
              provider={mapping.provider}
              size={18}
              isHovered={false}
              zIndex={0}
              borderColor="rgba(247, 247, 247, 1)"
              marginLeft={1}
              isReels={tactic === BrandPlanTactic.InstagramReel}
            />
          )
        })}
      </Button>
      <Menu
        aria-label="Video platforms"
        onAction={(key: Key) => onToggle(key as BrandPlanTactic)}
        showCheckmark
        selectionMode="multiple"
        selectedKeys={new Set(activeTactics)}
        disabledKeys={activeTactics.length === 1 ? [activeTactics[0]] : undefined}
      >
        <Item key="instagram_reel" textValue="Reels" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.Instagram} size={18} isHovered={false} marginLeft={0} />
          Reels
        </Item>
        <Item key="tiktok" textValue="TikTok" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.TikTok} size={18} marginLeft={0} />
          TikTok
        </Item>
        <Item key="youtube" textValue="YouTube" data-close-on-select={false}>
          <ProviderIcon provider={ExternalIntegrationEnum.YouTube} size={18} marginLeft={0} />
          YouTube
        </Item>
      </Menu>
    </MenuTrigger>
  )
}

const BLOG_PLATFORM_DISPLAY: Record<string, { name: string; icon: React.FC<{ size?: number }> }> = {
  [ExternalIntegrationEnum.GoHighLevel]: { name: 'GoHighLevel', icon: GoHighLevelBrand },
  [ExternalIntegrationEnum.Wix]: { name: 'Wix', icon: WixBrand },
  [ExternalIntegrationEnum.Shopify]: { name: 'Shopify', icon: ShopifyBrand },
  [ExternalIntegrationEnum.WordPress]: { name: 'WordPress', icon: WordPressBrand },
}

const BlogChannelDynamic = ({ provider }: { provider?: string | null }) => {
  const display =
    (provider && BLOG_PLATFORM_DISPLAY[provider]) || BLOG_PLATFORM_DISPLAY[ExternalIntegrationEnum.WordPress]
  const Icon = display.icon
  return (
    <div className={Styles.staticChannel}>
      <Icon size={18} />
      <span className={Styles.platformLabel}>{display.name}</span>
    </div>
  )
}

const EmailChannelStatic = () => (
  <div className={Styles.staticChannel}>
    <MailchimpBrand size={18} />
    <span className={Styles.platformLabel}>Mailchimp</span>
  </div>
)

export const TopicConfigurator = ({
  topicRows,
  setTopicRows,
  targetSchedule,
  onBack,
  onConfirm,
  onAddTopic,
  isLoading,
  needsCredits,
  creditsNeeded,
  hidePostTypeColumn,
}: TopicConfiguratorProps) => {
  const { closeModal, openModal: openChildModal } = useModals()
  const { mutateAsync: createMediaFile } = useCreateMediaFile()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProject = useUploadsProject(workspaceId)?.data?.project
  const { connectedBlogIntegration } = useBlogIntegrations()

  const openMediaModal = (rowId: string) => {
    openChildModal(
      ImageMenuModal,
      {
        onPressOutside: closeModal,
        accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG + ',' + ACCEPTED_VIDEO_MIME_TYPES,
        hiddenProviders: ['gifs'],
        uploadMedia: true,
        onUse: async (images) => {
          if (!uploadsProject) {
            showToastMessage('Unable to add media. Try again.', 'error')
            return
          }
          const dismiss = showToastMessage('Processing...', 'loading', undefined, 0)
          const newFiles: { id: string; thumbnail_url?: string; source_url?: string; duration?: number }[] = []

          await Promise.all(
            images.map(async (file) => {
              const mediaFile = await createMediaFile({
                source_url: file.source_url || file.url,
                mime_type: file.mime_type || 'image/jpeg',
                title: file.original_filename || file.url,
                project_key: uploadsProject.id,
                show_in_file_system: false,
              })
              const { id, thumbnail_url, source_url } = mediaFile
              newFiles.push({ id, source_url, thumbnail_url, duration: file.duration })
            }),
          )

          setTopicRows((prev) =>
            prev.map((row) => {
              if (row.id !== rowId) return row
              const existingFiles = row.mediaFiles || []
              const existingIds = row.mediaFileIds || []
              return {
                ...row,
                mediaFiles: [...existingFiles, ...newFiles],
                mediaFileIds: [...existingIds, ...newFiles.map((f) => f.id)],
              }
            }),
          )
          dismiss()
        },
        maxCount: Infinity,
      },
      { newStack: true },
    )
  }

  const removeMediaFile = (rowId: string, mediaFileId: string | number) => {
    setTopicRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row
        return {
          ...row,
          mediaFiles: (row.mediaFiles || []).filter((f) => f.id !== mediaFileId),
          mediaFileIds: (row.mediaFileIds || []).filter((id) => id !== mediaFileId),
        }
      }),
    )
  }

  const topicsStillLoading = topicRows.some((row) => row.loading)

  const updateRow = (id: string, updates: Partial<TopicRow>) => {
    setTopicRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...updates } : row)))
  }

  const removeRow = (id: string) => {
    setTopicRows((prev) => prev.filter((row) => row.id !== id))
  }

  const handlePostTypeChange = (rowId: string, newPostType: PostType) => {
    const scheduleTactics = targetSchedule.tactics || []
    const newTactics = getDefaultTacticsForPostType(newPostType, scheduleTactics)
    updateRow(rowId, { postType: newPostType, tactics: newTactics })
  }

  const handleTacticToggle = (rowId: string, tactic: BrandPlanTactic) => {
    const row = topicRows.find((r) => r.id === rowId)
    if (!row) return
    const tacticGroup = row.postType === 'social_post' ? SOCIAL_TACTICS : VIDEO_TACTICS
    const activeTactics = row.tactics.filter((t) => tacticGroup.includes(t))
    if (activeTactics.includes(tactic) && activeTactics.length === 1) return
    const newTactics = row.tactics.includes(tactic) ? row.tactics.filter((t) => t !== tactic) : [...row.tactics, tactic]
    updateRow(rowId, { tactics: newTactics })
  }

  const tomorrow = dayjs().add(1, 'day').startOf('day')
  const disableDatesBefore = tomorrow.format('YYYY-MM-DD')
  const campaignEnd = dayjs(targetSchedule.end_date)
  const disableDatesAfter = campaignEnd.isAfter(tomorrow) ? campaignEnd.format('YYYY-MM-DD') : undefined

  const scheduleTactics = targetSchedule.tactics || []

  return (
    <>
      <Modal.Header title="Configure Posts" />
      <Modal.Content>
        <div className={Styles.container}>
          <div className={cx(Styles.columnHeaders, { [Styles.noPostType]: hidePostTypeColumn })}>
            <Text variant="label" color="var(--dark-40)">
              Post topic
            </Text>
            {!hidePostTypeColumn && (
              <Text variant="label" color="var(--dark-40)">
                Post Type
              </Text>
            )}
            <Text variant="label" color="var(--dark-40)">
              Channels
            </Text>
            <Text variant="label" color="var(--dark-40)">
              Post date
            </Text>
            <div />
          </div>

          {topicRows.map((row) => {
            const currentPostType = POST_TYPE_OPTIONS.find((o) => o.value === row.postType) || POST_TYPE_OPTIONS[0]
            const CurrentIcon = currentPostType.icon

            return (
              <div key={row.id} className={cx(Styles.topicRow, { [Styles.noPostType]: hidePostTypeColumn })}>
                <div className={Styles.colTopic}>
                  {row.loading ? (
                    <ThemeLoadingRow phases={TOPIC_LOADING_PHASES} />
                  ) : (
                    <textarea
                      className={Styles.topicInput}
                      placeholder="Enter a topic..."
                      value={row.topic}
                      onChange={(e) => updateRow(row.id, { topic: e.target.value, userEdited: true })}
                    />
                  )}
                  {!row.loading && (
                    <>
                      {(row.mediaFiles || []).length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(row.mediaFiles || []).map((file) => (
                            <Image
                              key={file.id}
                              src={file.thumbnail_url || file.source_url}
                              duration={file.duration}
                              size="32px"
                              closeCoordinates={{ top: '2px', right: '2px' }}
                              onRemove={() => removeMediaFile(row.id, file.id)}
                            />
                          ))}
                        </div>
                      )}
                      <Button
                        variant="ghost-tertiary"
                        size="xs"
                        frontIcon={Plus}
                        onPress={() => openMediaModal(row.id)}
                        className={Styles.addContextButton}
                      >
                        Add Media
                      </Button>
                    </>
                  )}
                </div>

                {!hidePostTypeColumn && (
                  <div className={Styles.colPostType}>
                    <MenuTrigger placement="bottom-start">
                      <Button variant="secondary" withChevron="down">
                        <span className={Styles.postTypeLabel}>
                          <CurrentIcon size={14} color={currentPostType.color} />
                          {currentPostType.label}
                        </span>
                      </Button>
                      <Menu
                        aria-label="Post type"
                        onAction={(key: Key) => handlePostTypeChange(row.id, key as PostType)}
                        selectionMode="single"
                        selectedKeys={new Set([row.postType])}
                      >
                        {POST_TYPE_OPTIONS.map((opt) => (
                          <Item key={opt.value} textValue={opt.label}>
                            <opt.icon size={14} color={opt.color} />
                            {opt.label}
                          </Item>
                        ))}
                      </Menu>
                    </MenuTrigger>
                  </div>
                )}

                <div className={Styles.colChannels}>
                  {row.postType === 'social_post' && (
                    <SocialChannelDropdown
                      tactics={row.tactics}
                      scheduleTactics={scheduleTactics}
                      onToggle={(tactic) => handleTacticToggle(row.id, tactic)}
                    />
                  )}
                  {row.postType === 'video' && (
                    <VideoChannelDropdown
                      tactics={row.tactics}
                      scheduleTactics={scheduleTactics}
                      onToggle={(tactic) => handleTacticToggle(row.id, tactic)}
                    />
                  )}
                  {row.postType === 'blog_post' && <BlogChannelDynamic provider={connectedBlogIntegration?.provider} />}
                  {row.postType === 'email' && <EmailChannelStatic />}
                </div>

                <div className={Styles.colDate}>
                  <DateTimePicker
                    dateTime={row.postAt}
                    setDateTime={(dateTime) => {
                      if (!dateTime) return
                      updateRow(row.id, { postAt: dateTime })
                    }}
                    disableDatesBefore={disableDatesBefore}
                    disableDatesAfter={disableDatesAfter}
                  >
                    <Button withChevron="down" variant="secondary" size="sm">
                      {row.postAt.format('MMM D, h:mma')}
                    </Button>
                  </DateTimePicker>
                </div>

                <div className={Styles.colAction}>
                  {topicRows.length > 1 && (
                    <IconButton icon={Trash2} variant="tertiary" size="sm" onPress={() => removeRow(row.id)} />
                  )}
                </div>
              </div>
            )
          })}

          <Button
            variant="ghost-tertiary"
            size="md"
            frontIcon={Plus}
            onPress={onAddTopic}
            className={Styles.addTopicButton}
          >
            New Topic
          </Button>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={onBack || closeModal}>
            {onBack ? 'Back' : 'Cancel'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            size="lg"
            onPress={onConfirm}
            isDisabled={isLoading || topicsStillLoading || topicRows.length === 0}
          >
            {isLoading ? (
              'Generating...'
            ) : (
              <div className={Styles.buttonWithCredits}>
                <span>{needsCredits ? 'Add Credits to Generate' : 'Generate Content'}</span>
                {creditsNeeded > 0 && (
                  <>
                    <Credits color="var(--light-100)" />
                    <span>{creditsNeeded}</span>
                  </>
                )}
              </div>
            )}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
